import ky from 'ky';
import Cookies from 'universal-cookie';

import { COOKIE_USER_TOKEN } from '@/constants/cookie.constants';

import {
    CustomerDocumentRequestResponse,
    ListDocumentRequestsParams,
    SubmitDocumentParams,
} from './DocumentUploadApi.types';

const cookies = new Cookies();

/**
 * Client-side function to submit a document with upload progress tracking
 * Uses ky's onUploadProgress option to track upload progress
 */
export const submitDocumentWithProgress = async (
  requestId: string,
  approvalId: string,
  params: SubmitDocumentParams,
  onProgress?: (progress: number) => void,
): Promise<CustomerDocumentRequestResponse> => {
  const baseUrl = process.env.NEXT_PUBLIC_AUTH_BASE_API_URL || '';
  const apiBaseUrl = baseUrl.replace(/\/api\/v2$/, '').replace(/\/api$/, '') + '/api';

  const formData = new FormData();

  if (params.file) {
    formData.append('file', params.file);
  }

  if (params.existing_document_id) {
    formData.append('existing_document_id', params.existing_document_id);
  }
  if (params.content_type) {
    formData.append('content_type', params.content_type);
  }

  if (!params.file && !params.existing_document_id) {
    throw new Error('Either file or existing_document_id must be provided');
  }

  const token = cookies.get(COOKIE_USER_TOKEN);
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const url = `${apiBaseUrl}/customer-documents/requests/${requestId}/approvals/${approvalId}/documents`;

  const response = await ky.post(url, {
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    timeout: 300000,
    throwHttpErrors: false,
    onUploadProgress: onProgress
      ? (progress) => {
          if (onProgress) {
            const percentComplete = progress.percent * 100;
            onProgress(percentComplete);
          }
        }
      : undefined,
  });

  // Parse JSON manually to preserve large integer precision
  const responseText = await response.text();
  const preservedText = responseText.replaceAll(/:\s*(\d{17,})/g, (_match, numberStr) => `: "${numberStr}"`);
  const parsed = JSON.parse(preservedText);
  const data: CustomerDocumentRequestResponse = Array.isArray(parsed) ? parsed[0] : parsed;

  if (data?.id && data.customer_document_approvals) {
    return data;
  }

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  return data;
};

export const submitDocument = async (
  requestId: string,
  approvalId: string,
  params: SubmitDocumentParams,
): Promise<CustomerDocumentRequestResponse> => {
  const baseUrl = process.env.NEXT_PUBLIC_AUTH_BASE_API_URL || '';
  const apiBaseUrl = baseUrl.replace(/\/api\/v2$/, '').replace(/\/api$/, '') + '/api';

  const formData = new FormData();

  if (params.file) {
    formData.append('file', params.file);
  }

  if (params.existing_document_id) {
    formData.append('existing_document_id', params.existing_document_id);
  }
  if (params.content_type) {
    formData.append('content_type', params.content_type);
  }

  if (!params.file && !params.existing_document_id) {
    throw new Error('Either file or existing_document_id must be provided');
  }

  const token = cookies.get(COOKIE_USER_TOKEN);
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const url = `${apiBaseUrl}/customer-documents/requests/${requestId}/approvals/${approvalId}/documents`;

  const response = await ky.post(url, {
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    timeout: 15000,
    throwHttpErrors: false,
  });

  // Parse JSON manually to preserve large integer precision
  const responseText = await response.text();
  const preservedText = responseText.replaceAll(/:\s*(\d{17,})/g, (_match, numberStr) => `: "${numberStr}"`);
  const parsed = JSON.parse(preservedText);
  const data: CustomerDocumentRequestResponse = Array.isArray(parsed) ? parsed[0] : parsed;

  if (data?.id && data.customer_document_approvals) {
    return data;
  }

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  return data;
};

/**
 * Client-side function to fetch document requests
 */
export const getDocumentRequests = async (
  params?: ListDocumentRequestsParams,
): Promise<CustomerDocumentRequestResponse[]> => {
  const baseUrl = process.env.NEXT_PUBLIC_AUTH_BASE_API_URL || '';
  const apiBaseUrl = baseUrl.replace(/\/api\/v2$/, '').replace(/\/api$/, '') + '/api';

  const token = cookies.get(COOKIE_USER_TOKEN);
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const searchParams = new URLSearchParams();
  if (params?.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }
  if (params?.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }
  if (params?.order_id) {
    searchParams.append('order_id', params.order_id);
  }

  const queryString = searchParams.toString();
  const queryPart = queryString ? '?' + queryString : '';
  const url = `${apiBaseUrl}/customer-documents/requests${queryPart}`;

  const response = await ky.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    timeout: 20000,
  });

  const responseText = await response.text();
  const preservedText = responseText.replaceAll(/:\s*(\d{17,})/g, (_match, numberStr) => `: "${numberStr}"`);
  return JSON.parse(preservedText) as CustomerDocumentRequestResponse[];
};

/**
 * Client-side function to delete a document
 * Returns the updated document request data
 */
export const deleteDocument = async (
  requestId: string,
  approvalId: string,
  documentId: string,
): Promise<CustomerDocumentRequestResponse> => {
  const baseUrl = process.env.NEXT_PUBLIC_AUTH_BASE_API_URL || '';
  const apiBaseUrl = baseUrl.replace(/\/api\/v2$/, '').replace(/\/api$/, '') + '/api';

  const token = cookies.get(COOKIE_USER_TOKEN);
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const url = `${apiBaseUrl}/customer-documents/requests/${requestId}/approvals/${approvalId}/documents/${documentId}`;

  const response = await ky.delete(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    timeout: 15000,
  });

  // Parse JSON manually to preserve large integer precision
  const responseText = await response.text();
  const preservedText = responseText.replaceAll(/:\s*(\d{17,})/g, (_match, numberStr) => `: "${numberStr}"`);
  const parsed = JSON.parse(preservedText);
  return Array.isArray(parsed) ? parsed[0] : parsed;
};
